import type { AreaKind } from './areaCatalog';
import type { V2ChecklistItem, V2Property } from './inspection';

// NY mandatory descriptor catalogue — KNOWLEDGE IMPORT, DATA (docs/KNOWLEDGE-IMPORT-MANIFEST.md K-21).
//
// The 27 facts a New York home inspection report must DESCRIBE even when everything is normal,
// enumerated from the frozen verbatim text of 19 NYCRR 197-5.7 … 197-5.15 by the F1 report authority
// (docs/authority/ny/NY-REPORT-AUTHORITY.md §8) and fixed as the descriptor mechanism by the Owner on
// 2026-09-06 (OD-1 (a) = A1 structured descriptor fields; master §15.6). Nothing here adds a legal
// descriptor beyond that list, and the rule references are the authority's citation strings.
//
// Owner product constraints (binding): a descriptor never adds a form of its own — it rides on a
// checklist row the inspector already opens (`rows`) or on a property fact asked once at setup
// (`scope: 'property'`); only the descriptors of the current row are ever shown; choices are tapped,
// text stays one short line; a chosen Unknown is RESOLVED only where the Authority admits it as a
// printed value; an unanswered field is never silently turned into Unknown.
//
// This module is pure DATA + pure functions: no store, no i18n, no network. Labels live in the two
// dictionaries (`descriptor.<key>`, `descriptorChoice.<key>.<choice>`).

// Property facts (#9 / #10 / #11) — closed enumerations.
// 197-5.10(a)(1) requires the heating fuel to be described: 'unknown' records "asked, undetermined"
// and does NOT resolve the descriptor. 197-5.8(d) admits public / private / unknown as printed values.
export type HeatingFuel = 'gas' | 'oil' | 'electric' | 'propane' | 'other' | 'unknown';
export const HEATING_FUEL_ORDER: readonly HeatingFuel[] = Object.freeze(['gas', 'oil', 'electric', 'propane', 'other', 'unknown']);
export function isHeatingFuel(value: unknown): value is HeatingFuel {
  return typeof value === 'string' && (HEATING_FUEL_ORDER as readonly string[]).includes(value);
}

export type UtilitySource = 'public' | 'private' | 'unknown';
export const UTILITY_SOURCE_ORDER: readonly UtilitySource[] = Object.freeze(['public', 'private', 'unknown']);
export function isUtilitySource(value: unknown): value is UtilitySource {
  return typeof value === 'string' && (UTILITY_SOURCE_ORDER as readonly string[]).includes(value);
}

// Fuels that burn: they are what makes the CK-02 fuel shut-off row due.
const FUEL_BURNING: readonly string[] = Object.freeze(['gas', 'oil', 'propane']);

export type DescriptorKey =
  | 'roofMaterialCondition'
  | 'roofObservationMethod'
  | 'waterSupplyPipingMaterial'
  | 'dwvPipingMaterial'
  | 'waterHeaterCapacity'
  | 'waterHeaterEnergySource'
  | 'mainWaterShutoffLocation'
  | 'mainFuelShutoffLocation'
  | 'heatingFuel'
  | 'waterSupplySource'
  | 'wasteDisposalSource'
  | 'serviceAmperageVoltage'
  | 'disconnectLocations'
  | 'aluminumBranchWiring'
  | 'smokeDetectorPresence'
  | 'coDetectorPresence'
  | 'branchConductorCondition'
  | 'heatingEquipmentType'
  | 'heatingDistribution'
  | 'heatSourceEachHabitableSpace'
  | 'coolingEquipmentType'
  | 'coolingDistribution'
  | 'interiorSurfaces'
  | 'insulationUnfinishedSpaces'
  | 'ventilationAtticFoundation'
  | 'atticObservationMethod'
  | 'atticConditionsObserved';

export type DescriptorScope = 'property' | 'row';
export type DescriptorKind = 'choice' | 'text';

export interface DescriptorDefinition {
  key: DescriptorKey;
  scope: DescriptorScope;
  kind: DescriptorKind;
  // 19 NYCRR citation, as the F1 report authority writes it.
  ruleRef: string;
  // The F1 gap this descriptor closes.
  gapId: string;
  // Template rows the descriptor rides on (row scope). A row may carry more than one descriptor.
  rows: readonly string[];
  // Choice kind: the lawful values, in tap order. Text kind: empty.
  choices: readonly string[];
  // Choice values that record "asked, undetermined" and do NOT resolve the descriptor.
  unresolvedChoices: readonly string[];
  // The answer may carry one short free-text companion (a location, which rooms, "other" spelled out).
  detail: boolean;
  // Choice values that ask the inspector to record a safety finding (197-5.3(b)); never a limitation.
  promptsSafetyFindingOn: readonly string[];
  // The row control is prefilled from the property's heating fuel (#9 → #6).
  prefillFromHeatingFuel: boolean;
}

function def(
  key: DescriptorKey,
  ruleRef: string,
  gapId: string,
  shape: { scope?: DescriptorScope; rows?: readonly string[]; choices?: readonly string[]; unresolvedChoices?: readonly string[]; detail?: boolean; promptsSafetyFindingOn?: readonly string[]; prefillFromHeatingFuel?: boolean }
): DescriptorDefinition {
  const choices = Object.freeze([...(shape.choices ?? [])]);
  return Object.freeze({
    key,
    scope: shape.scope ?? 'row',
    kind: choices.length > 0 ? 'choice' : 'text',
    ruleRef,
    gapId,
    rows: Object.freeze([...(shape.rows ?? [])]),
    choices,
    unresolvedChoices: Object.freeze([...(shape.unresolvedChoices ?? [])]),
    detail: shape.detail ?? false,
    promptsSafetyFindingOn: Object.freeze([...(shape.promptsSafetyFindingOn ?? [])]),
    prefillFromHeatingFuel: shape.prefillFromHeatingFuel ?? false
  });
}

// The catalogue, in the F1 report authority's §8 / plan §11 order (#1 … #27).
export const DESCRIPTOR_CATALOGUE: readonly DescriptorDefinition[] = Object.freeze([
  def('roofMaterialCondition', '197-5.7(c)', 'RP-06', { rows: ['roof-covering'] }),
  def('roofObservationMethod', '197-5.7(b),(c)', 'RP-06', { rows: ['roof-method'], choices: ['walked', 'ladderAtEave', 'groundZoom', 'drone', 'notObserved'] }),
  def('waterSupplyPipingMaterial', '197-5.8(d)', 'RP-07', { rows: ['plb-supply-piping'] }),
  def('dwvPipingMaterial', '197-5.8(d)', 'RP-07', { rows: ['plb-drain-piping'] }),
  def('waterHeaterCapacity', '197-5.8(d)', 'RP-07', { rows: ['wh-data-plate'] }),
  def('waterHeaterEnergySource', '197-5.8(d)', 'RP-07', { rows: ['wh-data-plate'], choices: ['gas', 'oil', 'electric', 'propane', 'other'], prefillFromHeatingFuel: true }),
  def('mainWaterShutoffLocation', '197-5.8(d)', 'RP-07', { rows: ['plb-main-shutoff'] }),
  def('mainFuelShutoffLocation', '197-5.8(d)', 'RP-07', { rows: ['plb-fuel-shutoff'] }),
  def('heatingFuel', '197-5.10(a)(1)', 'RP-09', { scope: 'property', choices: HEATING_FUEL_ORDER, unresolvedChoices: ['unknown'] }),
  def('waterSupplySource', '197-5.8(d)', 'RP-07', { scope: 'property', choices: UTILITY_SOURCE_ORDER }),
  def('wasteDisposalSource', '197-5.8(d)', 'RP-07', { scope: 'property', choices: UTILITY_SOURCE_ORDER }),
  def('serviceAmperageVoltage', '197-5.9(b)(1)', 'RP-08', { rows: ['elec-panel-label'] }),
  def('disconnectLocations', '197-5.9(b)(2)', 'RP-08', { rows: ['elec-panel-overview'] }),
  def('aluminumBranchWiring', '197-5.9(b)(3)', 'RP-08', { rows: ['elec-wiring-type'], choices: ['present', 'notObserved'] }),
  def('smokeDetectorPresence', '197-5.9(b)(4)', 'RP-08', { rows: ['safe-alarms-system'], choices: ['present', 'absent', 'partial'], detail: true, promptsSafetyFindingOn: ['absent'] }),
  def('coDetectorPresence', '197-5.9(b)(4)', 'RP-08', { rows: ['safe-alarms-system'], choices: ['present', 'absent', 'partial'], detail: true, promptsSafetyFindingOn: ['absent'] }),
  def('branchConductorCondition', '197-5.9(b)(5)', 'RP-08', { rows: ['elec-wiring-type'] }),
  def('heatingEquipmentType', '197-5.10(a)(1)', 'RP-09', { rows: ['hvac-heating-unit'] }),
  def('heatingDistribution', '197-5.10(a)(1)', 'RP-09', { rows: ['hvac-distribution'], choices: ['forcedAir', 'hydronic', 'steam', 'radiant', 'baseboard', 'other'], detail: true }),
  def('heatSourceEachHabitableSpace', '197-5.10(a)(6)', 'RP-09', { rows: ['hvac-distribution'], choices: ['allSpaces', 'someSpaces', 'none'], detail: true }),
  def('coolingEquipmentType', '197-5.11(a)(1)', 'RP-10', { rows: ['hvac-ac-unit'] }),
  def('coolingDistribution', '197-5.11(a)(1)', 'RP-10', { rows: ['hvac-ac-unit'], choices: ['ducted', 'ductless', 'roomUnits', 'other'], detail: true }),
  def('interiorSurfaces', '197-5.12(a)(1)', 'RP-11', { rows: ['int-surfaces'] }),
  def('insulationUnfinishedSpaces', '197-5.13(a)(1)', 'RP-12', { rows: ['attic-insulation', 'bsmt-foundation-insulation'] }),
  def('ventilationAtticFoundation', '197-5.13(a)(2)', 'RP-12', { rows: ['attic-ventilation', 'bsmt-foundation-insulation'] }),
  def('atticObservationMethod', '197-5.15(a)(1)', 'RP-13', { rows: ['attic-access'], choices: ['entered', 'fromHatch', 'notObserved'] }),
  def('atticConditionsObserved', '197-5.15(a)(2)', 'RP-13', { rows: ['attic-access'] })
]);

export const DESCRIPTOR_CATALOGUE_SIZE = 27;

const BY_KEY: ReadonlyMap<DescriptorKey, DescriptorDefinition> = new Map(DESCRIPTOR_CATALOGUE.map((d) => [d.key, d] as const));

const BY_ROW: ReadonlyMap<string, readonly DescriptorDefinition[]> = (() => {
  const map = new Map<string, DescriptorDefinition[]>();
  for (const d of DESCRIPTOR_CATALOGUE) for (const row of d.rows) map.set(row, [...(map.get(row) ?? []), d]);
  return map;
})();

export function isDescriptorKey(value: unknown): value is DescriptorKey {
  return typeof value === 'string' && BY_KEY.has(value as DescriptorKey);
}

export function descriptorDefinition(key: DescriptorKey): DescriptorDefinition {
  const found = BY_KEY.get(key);
  if (!found) throw new Error(`unknown descriptor ${key}`);
  return found;
}

// The descriptors a checklist row carries, in catalogue order. Custom rows carry none.
export function descriptorsForTemplate(templateId: string | null): readonly DescriptorDefinition[] {
  return templateId ? (BY_ROW.get(templateId) ?? []) : [];
}

// The inspector's typed answer to one descriptor of one row. `value` = choice id or short text;
// `detail` = the optional companion line ('' when none).
export interface DescriptorAnswer {
  key: DescriptorKey;
  value: string;
  detail: string;
}

export function itemDescriptorAnswer(item: Pick<V2ChecklistItem, 'descriptors'>, key: DescriptorKey): DescriptorAnswer | null {
  return item.descriptors.find((d) => d.key === key) ?? null;
}

// RESOLVED (OC-2): a listed lawful choice, or a non-empty text. Never null, never an empty text,
// never a value outside the catalogue, never a choice the catalogue marks "asked, undetermined".
export function descriptorResolved(definition: DescriptorDefinition, answer: DescriptorAnswer | null): boolean {
  if (!answer || answer.key !== definition.key) return false;
  const value = answer.value.trim();
  if (definition.kind === 'text') return value.length > 0;
  return definition.choices.includes(value) && !definition.unresolvedChoices.includes(value);
}

export function propertyFactValue(property: Pick<V2Property, 'heatingFuel' | 'waterSupplySource' | 'wasteDisposalSource'>, key: DescriptorKey): string | null {
  switch (key) {
    case 'heatingFuel':
      return property.heatingFuel;
    case 'waterSupplySource':
      return property.waterSupplySource;
    case 'wasteDisposalSource':
      return property.wasteDisposalSource;
    default:
      return null;
  }
}

export function propertyFactResolved(definition: DescriptorDefinition, value: string | null): boolean {
  if (value === null) return false;
  return definition.choices.includes(value) && !definition.unresolvedChoices.includes(value);
}

export function descriptorPromptsSafetyFinding(definition: DescriptorDefinition, answer: DescriptorAnswer | null): boolean {
  return answer !== null && definition.promptsSafetyFindingOn.includes(answer.value);
}

// ---------- conditional rows (frozen plan §13: CK-02 · CK-03 · CK-07) ----------

export interface ConditionalRowTrigger {
  kind: AreaKind;
  templateId: string;
}

// Which of the conditional rows the recorded facts call for. Pure: the caller decides where
// (an existing area of that kind) and whether (once, never for a blocked inspection) to seed them.
//   CK-02  fuel storage / distribution + main fuel shut-off  ← a fuel-burning heating fuel (#9) or water heater (#6)
//   CK-03  fixed supplementary heat units                     ← rooms without a heat source from the main system (#20)
//   CK-07  foundation-area insulation / ventilation           ← a basement that is not fully finished
//   CK-09  well surface indications                           ← water supply answered "private" (#10)
//   CK-09  septic surface indications                         ← waste disposal answered "private" (#11)
// CK-09 (Owner ruling 2026-09-09): only the SURFACE INDICATIONS are observed; neither system is
// evaluated. A municipal ('public') or undetermined ('unknown') answer never seeds these rows.
export function conditionalRowsDue(input: { property: Pick<V2Property, 'heatingFuel' | 'basementFinish' | 'waterSupplySource' | 'wasteDisposalSource'>; items: readonly Pick<V2ChecklistItem, 'templateId' | 'descriptors'>[] }): ConditionalRowTrigger[] {
  const out: ConditionalRowTrigger[] = [];
  const answer = (templateId: string, key: DescriptorKey): string | null => {
    for (const item of input.items) {
      if (item.templateId !== templateId) continue;
      const found = item.descriptors.find((d) => d.key === key);
      if (found) return found.value;
    }
    return null;
  };
  const fuelHeat = input.property.heatingFuel !== null && FUEL_BURNING.includes(input.property.heatingFuel);
  const waterHeaterFuel = answer('wh-data-plate', 'waterHeaterEnergySource');
  if (fuelHeat || (waterHeaterFuel !== null && FUEL_BURNING.includes(waterHeaterFuel))) out.push({ kind: 'plumbing', templateId: 'plb-fuel-shutoff' });
  const heatSource = answer('hvac-distribution', 'heatSourceEachHabitableSpace');
  if (heatSource === 'someSpaces' || heatSource === 'none') out.push({ kind: 'hvac', templateId: 'hvac-supplementary-heat' });
  const finish = input.property.basementFinish;
  if (finish === 'unfinished' || finish === 'partial' || finish === 'mixed') out.push({ kind: 'basement', templateId: 'bsmt-foundation-insulation' });
  if (input.property.waterSupplySource === 'private') out.push({ kind: 'exterior', templateId: 'ext-well' });
  if (input.property.wasteDisposalSource === 'private') out.push({ kind: 'exterior', templateId: 'ext-septic' });
  return out;
}
